import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Webhook, Plus, Trash2, Activity, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

export default function WebhooksPage({ user, onLogout }) {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');

  const fetchWebhooks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/webhooks`, { headers: getAuthHeaders() });
      setWebhooks(response.data);
    } catch (error) {
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const createWebhook = async (e) => {
    e.preventDefault();
    if (!webhookUrl.trim()) {
      toast.error('Please enter a webhook URL');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/webhooks`,
        { url: webhookUrl, enabled: true, events: ['high_risk_match'] },
        { headers: getAuthHeaders() }
      );
      setWebhooks([...webhooks, response.data]);
      setWebhookUrl('');
      setDialogOpen(false);
      toast.success('Webhook created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    }
  };

  const toggleWebhook = async (webhookId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/webhooks/${webhookId}?enabled=${!currentStatus}`,
        {},
        { headers: getAuthHeaders() }
      );
      setWebhooks(webhooks.map(w => w.id === webhookId ? { ...w, enabled: !currentStatus } : w));
      toast.success(`Webhook ${!currentStatus ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update webhook');
    }
  };

  const deleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await axios.delete(`${API}/webhooks/${webhookId}`, { headers: getAuthHeaders() });
      setWebhooks(webhooks.filter(w => w.id !== webhookId));
      toast.success('Webhook deleted');
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="webhooks">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" data-testid="webhooks-title">
              Webhook Notifications
            </h1>
            <p className="text-sm text-slate-600 mt-2">Receive real-time alerts for high-risk matches</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="create-webhook-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Webhook
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Webhook</DialogTitle>
              </DialogHeader>
              <form onSubmit={createWebhook} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="webhook-url">Webhook URL</Label>
                  <Input
                    id="webhook-url"
                    type="url"
                    placeholder="https://your-domain.com/webhook"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    className="mt-2"
                    data-testid="input-webhook-url"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Webhooks will be triggered for high-risk matches
                  </p>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-webhook">
                  Create Webhook
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Card */}
        <Card className="border border-blue-200 bg-blue-50 p-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">How Webhooks Work</h3>
              <p className="text-sm text-blue-700 mb-2">
                Webhooks send HTTP POST requests to your specified URL when high-risk matches are detected.
              </p>
              <p className="text-sm text-blue-700">
                Payload format: <code className="bg-white px-2 py-1 rounded font-mono text-xs">{'{ "event": "high_risk_match", "timestamp": "...", "data": {...} }'}</code>
              </p>
            </div>
          </div>
        </Card>

        {/* Webhooks List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading webhooks...</div>
        ) : webhooks.length === 0 ? (
          <Card className="p-12 text-center border border-slate-200">
            <Webhook className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-4">No webhooks configured yet.</p>
            <p className="text-sm text-slate-400">Add a webhook to receive real-time notifications for high-risk matches.</p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="webhooks-list">
            {webhooks.map((webhook) => (
              <Card key={webhook.id} className="border border-slate-200 shadow-sm" data-testid={`webhook-card-${webhook.id}`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Webhook className="w-4 h-4 text-slate-400 mr-2" />
                        <code className="text-sm font-mono text-slate-900">{webhook.url}</code>
                      </div>
                      <div className="flex items-center space-x-4 text-xs text-slate-500">
                        <span>Created: {new Date(webhook.created_at).toLocaleDateString()}</span>
                        {webhook.last_triggered && (
                          <span className="flex items-center">
                            <Activity className="w-3 h-3 mr-1" />
                            Last triggered: {new Date(webhook.last_triggered).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Label htmlFor={`toggle-${webhook.id}`} className="text-sm">
                          {webhook.enabled ? 'Enabled' : 'Disabled'}
                        </Label>
                        <Switch
                          id={`toggle-${webhook.id}`}
                          checked={webhook.enabled}
                          onCheckedChange={() => toggleWebhook(webhook.id, webhook.enabled)}
                          data-testid={`toggle-webhook-${webhook.id}`}
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteWebhook(webhook.id)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-webhook-${webhook.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {webhook.events.map((event) => (
                      <span
                        key={event}
                        className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-semibold text-slate-600"
                      >
                        {event}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}